import React, { useEffect, useState, useRef } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, LayoutContent, LayoutBody, LayoutHeader } from "./layout";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
} from "./sidebar";
import { CompanySwitcher } from "./dropdown";
import SiderComponent from "../shared/SiderComponent";
import HeaderComponent from "../shared/HeaderComponent";
import MaitaiLogo from "../../assets/maitai_logo_full.svg";
import MaitaiLogoDark from "../../assets/maitai_logo_dark_transparent.svg";
import { useTheme } from "../../contexts/ThemeContext";
import { AssistantSidebar } from "../Assistant/AssistantSidebar";
import { AssistantLayout } from "./assistant-layout";
import { useAssistantSidebar } from "../../hooks/useAssistantSidebar";

interface AppLayoutProps {
  children: React.ReactNode;
  companies?: { company_name: string; key_value: string; id?: number }[];
  setMeta?: (meta: any) => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  companies = [],
  setMeta,
}) => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSidebar, toggleSidebar } = useAssistantSidebar();
  const hasRestoredRef = useRef(false);

  // Initialize both company name and API key from localStorage
  const savedApiKey = localStorage.getItem("selectedCompanyApiKey");
  const savedCompanyName = localStorage.getItem("selectedCompanyName");
  const baseApiKey = localStorage.getItem("baseCompanyApiKey");
  const baseCompanyName = localStorage.getItem("baseCompanyName");

  if (!baseApiKey && savedApiKey && savedCompanyName) {
    localStorage.setItem("baseCompanyApiKey", savedApiKey);
    localStorage.setItem("baseCompanyName", savedCompanyName);
  }

  const [companyName, setCompanyName] = useState<string>(() => {
    // Use saved company name if available
    if (savedCompanyName) {
      return savedCompanyName;
    }
    return "Change Environment";
  });

  useEffect(() => {
    // When companies are loaded, try to restore the saved company by name (only once)
    if (companies?.length > 0 && savedCompanyName && !hasRestoredRef.current) {
      const savedCompany = companies.find(
        (c) => c.company_name === savedCompanyName
      );
      if (savedCompany) {
        // Found the company, restore it
        console.log(
          `[AppLayout] Restoring company selection: ${savedCompany.company_name} (ID: ${savedCompany.id})`
        );
        setCompanyName(savedCompany.company_name);
        if (setMeta) {
          setMeta({
            api_key: savedCompany.key_value,
            company_id: savedCompany.id,
          });
        }
        // Update localStorage with the fresh key (in case it's an admin key that got re-encoded)
        if (typeof savedCompany.key_value === "string") {
          localStorage.setItem("selectedCompanyApiKey", savedCompany.key_value);
        }
        hasRestoredRef.current = true;
      } else {
        // Company no longer in the list, clear saved data
        console.log(
          `[AppLayout] Saved company "${savedCompanyName}" not found in list, resetting`
        );
        localStorage.removeItem("selectedCompanyApiKey");
        localStorage.removeItem("selectedCompanyName");
        setCompanyName("Change Environment");
        hasRestoredRef.current = true;
      }
    }
  }, [companies, setMeta, savedCompanyName]);

  const handleCompanyChange = (
    apiKey: ApiKeyOrGetter,
    companyName: string,
    companyId?: number
  ) => {
    console.log(
      `[AppLayout] Switching to company: ${companyName} (ID: ${companyId})`
    );
    if (setMeta) {
      setMeta({ api_key: apiKey, company_id: companyId });
      // Only store in localStorage if it's a string (API key), not an object (auth context)
      if (typeof apiKey === "string") {
        localStorage.setItem("selectedCompanyApiKey", apiKey);
      } else {
        // If switching back to auth context, clear the stored key
        localStorage.removeItem("selectedCompanyApiKey");
      }
    }
    setCompanyName(companyName);
    localStorage.setItem("selectedCompanyName", companyName);
  };

  const showCompanySwitcher =
    companies.length > 0 || baseCompanyName === "Maitai";

  const layoutContent = (
    <Layout>
      {/* Sidebar */}
      <Sidebar width={256}>
        {/* Logo Section */}
        <SidebarHeader>
          <img
            src={theme === "dark" ? MaitaiLogoDark : MaitaiLogo}
            alt="Maitai Logo"
            className="h-8 w-auto"
          />
        </SidebarHeader>

        {/* Navigation */}
        <SidebarContent>
          <SiderComponent />
        </SidebarContent>

        {/* Company Switcher */}
        {showCompanySwitcher && (
          <SidebarFooter>
            <CompanySwitcher
              currentCompany={companyName}
              companies={companies}
              onCompanyChange={handleCompanyChange}
            />
          </SidebarFooter>
        )}
      </Sidebar>

      {/* Main Content */}
      <LayoutContent>
        {/* Header */}
        <LayoutHeader>
          <HeaderComponent
            showAssistant={true}
            assistantOpen={showSidebar}
            onToggleAssistant={toggleSidebar}
          />
        </LayoutHeader>

        {/* Page Content */}
        <LayoutBody>{children}</LayoutBody>
      </LayoutContent>
    </Layout>
  );

  return (
    <AssistantLayout
      showSidebar={showSidebar}
      onToggleSidebar={toggleSidebar}
      sidebar={<AssistantSidebar />}>
      {layoutContent}
    </AssistantLayout>
  );
};
